import {
  Component,
  computed,
  effect,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  untracked,
  ViewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { EditionsApiService } from '@galaxy/marketplace-apps/v1';
import { TranslateService } from '@ngx-translate/core';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyDataSource, TableContentHeaderComponent } from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import {
  AppPricingApiService,
  ItemsApiService,
  ItemType,
  ListActivatableItemsSortField,
  ListActivatableItemsSortOptions,
  OrderFormFilter,
  PartnerApiService,
  SortDirection,
} from '@vendasta/marketplace-apps';
import { BehaviorSubject, Observable } from 'rxjs';
import { LineItem, LineItemCacheService } from '../services/line-item-cache.service';
import {
  ItemSelectorConfig,
  ItemSelectorFilters,
  ItemSelectorRowItem,
  ItemSelectorState,
  SelectedItem,
  SelectionChange,
  SelectionChangeEventType,
} from './interface';
import { ITEMS_SELECTOR_COLUMNS } from './item-selector-columns';
import { ItemSelectorServiceV2, SelectionEvent, SelectionOptions } from './item-selector-v2.service';
import { ItemSelectorDataSource } from './item-selector.data-source';
import { convertRowItemToSelectedItem } from './util/item';

@Component({
  selector: 'inventory-ui-item-selector',
  templateUrl: './item-selector.component.html',
  styleUrls: ['./item-selector.component.scss'],
  providers: [{ provide: MAT_CHECKBOX_DEFAULT_OPTIONS, useValue: { clickAction: 'noop' } }],
})
export class ItemSelectorComponent implements OnInit, OnChanges, OnDestroy {
  readonly ItemType = ItemType;
  readonly PopoverPositions = PopoverPositions;

  @Input() config?: ItemSelectorConfig;
  protected configuration: Required<ItemSelectorConfig> = DEFAULT_CONFIG;

  @Input() dependenciesLoading = false;

  @Input() set selectedItems(value: SelectedItem[]) {
    this.itemSelectorServiceV2.initSelectedItems(value);
  }

  @Input() set immutableSelectedItems(value: SelectedItem[]) {
    this.itemSelectorServiceV2.initImmutableSelectedItems(value);
  }

  @Output() selectionChange: EventEmitter<SelectionChange> = new EventEmitter<SelectionChange>();

  @ViewChild('tableHeader') tableHeader!: TableContentHeaderComponent;
  columns: GalaxyColumnDef[] = ITEMS_SELECTOR_COLUMNS;
  dataSource!: ItemSelectorDataSource;
  tableDataSource!: GalaxyDataSource<ItemSelectorRowItem, ItemSelectorFilters>;

  private sortOptions$$ = new BehaviorSubject<ListActivatableItemsSortOptions>(
    new ListActivatableItemsSortOptions({
      sortField: ListActivatableItemsSortField.LIST_ACTIVATABLE_ITEMS_SORT_FIELD_DEFAULT_NAME,
      sortDirection: SortDirection.ASCENDING,
    }),
  );

  private immutableSelectedItems$ = toObservable(this.itemSelectorServiceV2.immutableSelectedItems);
  private selectedItems$ = toObservable(this.itemSelectorServiceV2.selectedItems);

  private state = signal<ItemSelectorState>({
    itemType: 'products',
    requiredItem: null,
    showAddons: true,
    showUnavailableItems: false,
  });

  private filters = computed<ItemSelectorFilters>(() => {
    const state = this.state();

    return {
      itemTypes: calculateItemTypes(state.itemType, state.showAddons),
      includeSuspended: state.showUnavailableItems,
      requiredItemId: requiredItemFilter(state.itemType, state.requiredItem),
      country: state.showUnavailableItems ? '' : this.configuration.country,
      hasOrderForm: this.configuration.hideItemsWithOrderForms
        ? OrderFormFilter.ORDER_FORM_FILTER_NO_ORDER_FORM
        : OrderFormFilter.ORDER_FORM_FILTER_UNSPECIFIED,
    };
  });

  public showAddons = computed(() => this.state().showAddons);
  public requiredItem = computed(() => this.state().requiredItem);
  public showUnavailableItems = computed(() => this.state().showUnavailableItems);
  public itemType = computed(() => this.state().itemType);
  public itemTypeTranslationKey = computed(() => {
    const itemType = this.itemType();
    switch (itemType) {
      case 'products': {
        return 'COMMON.ITEM_TYPE.PRODUCTS';
      }
      case 'packages': {
        return 'COMMON.ITEM_TYPE.PACKAGES';
      }
      default: {
        return 'COMMON.ITEM_TYPE.ALL_ITEMS';
      }
    }
  });

  constructor(
    private itemsApiService: ItemsApiService,
    private appPricingApiService: AppPricingApiService,
    private itemSelectorServiceV2: ItemSelectorServiceV2,
    private editionsService: EditionsApiService,
    private partnerApiService: PartnerApiService,
    private lineItemCacheService: LineItemCacheService,
    private confirmationModalService: OpenConfirmationModalService,
    private translateService: TranslateService,
    private snackbarService: SnackbarService,
  ) {
    effect(() => {
      this.updateTableFilters();
    });

    effect(() => {
      const selectedItems = this.itemSelectorServiceV2.selectedItems();
      if (this.configuration?.businessId) {
        const lineItems: LineItem[] =
          selectedItems.map<LineItem>((selectedItem) => {
            const editionId = selectedItem.type === 'app' ? selectedItem.edition?.editionId : undefined;
            return {
              itemId: selectedItem.itemId,
              editionId: editionId,
              itemType: selectedItem.type,
              quantity: selectedItem.quantity,
            };
          }) || [];
        this.lineItemCacheService.cacheLineItems(this.configuration.businessId, lineItems);
      }
    });

    effect(() => {
      const selectionEvents = this.itemSelectorServiceV2.changeEvents();
      untracked(() => {
        const selectionEvent = selectionEvents[0];
        // Don't emit for duplicate item events
        if (selectionEvent?.eventSource === 'conflict-duplicate-item' && selectionEvent?.eventType === 'deselected') {
          return;
        }
        // Emit all other events, but only show snackbar for dependency events
        if (selectionEvent?.updatedItems?.length) {
          this.emitSelectionChanges(selectionEvent);
        }
        if (selectionEvent?.eventSource === 'user') {
          return;
        }

        this.notifyUserOfChanges(selectionEvent);
      });
    });
  }

  ngOnInit(): void {
    this.dataSource = new ItemSelectorDataSource(
      this.itemsApiService,
      this.appPricingApiService,
      this.editionsService,
      this.sortOptions$$.asObservable(),
      this.itemSelectorServiceV2,
      this.immutableSelectedItems$,
      this.selectedItems$,
      this.configuration,
      this.partnerApiService,
    );
    this.tableDataSource = new GalaxyDataSource<ItemSelectorRowItem, ItemSelectorFilters>(this.dataSource);
    this.setupColumns();
    this.updateTableFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.config) {
      const config = changes.config.currentValue as ItemSelectorConfig;
      this.configuration = {
        ...DEFAULT_CONFIG,
        ...config,
      };
      if (config?.itemTypesAvailable?.length) {
        this.setItemType(config.itemTypesAvailable[0]);
      }
    }
  }

  ngOnDestroy(): void {
    this.itemSelectorServiceV2.clearSelection();
  }

  setupColumns() {
    const hideRetailColumn = (column: GalaxyColumnDef) => column.id === 'retail' && this.config?.hideRetail;
    const hideWholesaleColumn = (column: GalaxyColumnDef) => column.id === 'wholesale' && this.config?.hideWholesale;

    this.columns = this.columns.filter((column) => !hideRetailColumn(column) && !hideWholesaleColumn(column));
  }

  updateTableFilters(): void {
    const filters = this.filters();
    this.tableDataSource?.setFilters([filters]);
  }

  handleSelectionChange(selected: boolean, rowItem: ItemSelectorRowItem): void {
    rowItem.quantity = selected ? 1 : 0;
    const selectedItem = convertRowItemToSelectedItem(rowItem);
    this.itemSelectorServiceV2.updateSelection(selected, selectedItem, this.selectionOptionsFromConfig());
  }

  handleEditionChange(editionId: string, rowItem: ItemSelectorRowItem): void {
    rowItem.editionId = editionId;
    const selectedItem = convertRowItemToSelectedItem(rowItem);
    this.itemSelectorServiceV2.updateItemEdition(selectedItem, this.selectionOptionsFromConfig());
  }

  handleQuantityChange(event: Event, rowItem: ItemSelectorRowItem): void {
    rowItem.quantity = (event.target as HTMLInputElement).valueAsNumber;
    const selectedItem = convertRowItemToSelectedItem(rowItem);
    this.itemSelectorServiceV2.updateItemQuantity(selectedItem, this.selectionOptionsFromConfig());
  }

  setShowUnavailableItems(value: boolean): void {
    this.state.update((s) => ({ ...s, showUnavailableItems: value }));
  }

  setShowAddons(value: boolean): void {
    this.state.update((s) => {
      s.showAddons = value;
      if (!value) {
        s.requiredItem = null;
      }
      return { ...s };
    });
  }

  setRequiredItem(item: ItemSelectorRowItem): void {
    this.state.update((s) => ({ ...s, requiredItem: item, showAddons: true }));
    this.tableHeader.setSearchTerm('');
  }

  unsetRequiredItem(): void {
    this.state.update((s) => ({ ...s, requiredItem: null }));
    this.tableHeader.setSearchTerm('');
  }

  private selectionOptionsFromConfig(): SelectionOptions {
    return {
      partnerId: this.configuration.partnerId,
      marketId: this.configuration.marketId,
      confirmItenComflictResolutionCallback: this.confirmItemConflictResolutionModal.bind(this),
      enforceDependencies: this.configuration.enforceDependencies,
    };
  }

  private confirmItemConflictResolutionModal(): Observable<boolean> {
    return this.confirmationModalService.openModal({
      title: this.translateService.instant('ITEM_SELECTOR.CONFLICT_RESOLUTION_MODAL.TITLE'),
      message: this.translateService.instant('ITEM_SELECTOR.CONFLICT_RESOLUTION_MODAL.MESSAGE'),
      confirmButtonText: this.translateService.instant('COMMON.ACTIONS.ADD_TO_ORDER'),
      type: 'confirm',
    });
  }

  private formatItemNames(selectedItems: SelectedItem[], includeEditions = true): string {
    return selectedItems
      .map((si) => {
        let name = si.name;
        if (si.type === 'app' && includeEditions && si.edition?.name) {
          name = `${si.name} | ${si.edition.name}`;
        }
        return name;
      })
      .join(', ');
  }

  private emitSelectionChanges(selectionEvent: SelectionEvent): void {
    selectionEvent.updatedItems.forEach((updatedItem) => {
      this.selectionChange.emit({
        selectedItems: this.itemSelectorServiceV2.selectedItems(),
        updatedItem: updatedItem,
        eventType:
          selectionEvent.eventType === 'selected'
            ? SelectionChangeEventType.SELECTED
            : SelectionChangeEventType.DESELECTED,
      });
    });
  }

  setItemType(type: 'products' | 'packages') {
    this.state.update((s) => ({ ...s, itemType: type, requiredItem: null }));
  }

  private notifyUserOfChanges(selectionEvent: SelectionEvent): void {
    switch (selectionEvent?.eventSource) {
      case 'dependency-selection-change':
        this.showSelectionChangeNotification(selectionEvent);
        break;
      case 'dependency-edition-change':
        this.showEditionChangeNotification(selectionEvent);
        break;
      case 'dependency-unavailable':
        this.snackbarService.openErrorSnack('ITEM_SELECTOR.DEPENDENCIES.REQUIRED_ITEMS_UNAVAILABLE');
        break;
    }
  }

  private showSelectionChangeNotification(selectionEvent: SelectionEvent): void {
    const translation =
      selectionEvent.eventType === 'selected'
        ? 'ITEM_SELECTOR.DEPENDENCIES.SELECTION_NOTIFICATION'
        : 'ITEM_SELECTOR.DEPENDENCIES.REMOVAL_NOTIFICATION';
    const notification = this.translateService.instant(translation, {
      itemNames: this.formatItemNames(selectionEvent.updatedItems),
    });
    this.snackbarService.openSuccessSnack(notification);
  }

  private showEditionChangeNotification(selectionEvent: SelectionEvent): void {
    if (selectionEvent.eventType !== 'deselected' || !selectionEvent.triggerItem) {
      return;
    }

    const translation = 'ITEM_SELECTOR.DEPENDENCIES.REMOVAL_DUE_TO_EDITION_CHANGE';
    const notification = this.translateService.instant(translation, {
      affectedItemNames: this.formatItemNames(selectionEvent.updatedItems),
      triggerItemName: this.formatItemNames([selectionEvent.triggerItem], false),
    });
    this.snackbarService.openSuccessSnack(notification);
  }
}

export const DEFAULT_CONFIG: Required<ItemSelectorConfig> = {
  partnerId: '',
  pageSize: 15,
  country: '',
  priceType: 'generic',
  marketId: '',
  businessId: '',
  hideOwnerName: false,
  hideWholesale: false,
  hideRetail: false,
  hideQuantity: false,
  hideItemsWithOrderForms: false,
  itemTypesAvailable: ['products'],
  enforceDependencies: false,
};

function calculateItemTypes(itemType: 'products' | 'packages' | 'allItems', showAddons: boolean) {
  let itemTypes = [ItemType.ITEM_TYPE_APP, ItemType.ITEM_TYPE_PACKAGE];

  if (itemType === 'packages') {
    return [ItemType.ITEM_TYPE_PACKAGE];
  }
  if (itemType === 'products') {
    itemTypes = [ItemType.ITEM_TYPE_APP];
  }

  if (showAddons) {
    itemTypes.push(ItemType.ITEM_TYPE_ADDON);
  }

  return itemTypes;
}

function requiredItemFilter(itemType: 'products' | 'packages' | 'allItems', requiredItem: ItemSelectorRowItem | null) {
  if (itemType === 'packages') {
    return '';
  }

  return requiredItem?.itemId ?? '';
}
